import { useCallback, useState } from "react";
import type { CSSProperties } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { formatDistanceToNow } from "date-fns";
import { BookmarkIcon, CompassIcon } from "lucide-react";

import {
  LabelChooser,
  LabelItem,
} from "simplydo/react/components/Choosers/LabelChooser/LabelChooser";
import { styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { H1 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Placeholder, PlaceholderLine } from "simplydo/react/ui/placeholder";
import { Popup } from "simplydo/react/ui/popup";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import {
  AnonymousChip,
  IdeaBusinessProfileChip,
  UserChip,
} from "components/lib/Chips";
import {
  EntityCover,
  EntityHeaderContent,
  EntityMetaBlock,
  EntityMetaChips,
  EntityMetaGrid,
  EntityMetaSectionLabel,
  EntityMetaSide,
  EntityPeopleRow,
} from "components/lib/EntityHeader";
import type {
  IdeaPreviewIdea,
  IdeaPreviewOwnerChallenge,
  IdeaPreviewProjectBoard,
} from "components/lib/Ideas/IdeaPreview/types";

import IdeaTags from "./Tags";
import { useIdea } from "./hooks/useIdea";

const StyledLaneInfo = styled.div`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  line-height: 1;
  color: #3a414b;
  font-size: 14px;
  > a {
    font-weight: 600;
    color: inherit;
  }
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: ${({ theme }) => (theme.sizes.isMobile ? 16 : 24)}px;
  padding-top: 16px;
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  flex: 1;

  h1 {
    margin: 0;
    font-size: ${({ theme }) => (theme.sizes.isMobile ? 28 : 40)}px;
    line-height: 1.1;
    font-weight: 650;
    letter-spacing: -0.02em;
    color: #111418;
  }
`;

const ContextRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #3a414b;
  flex-wrap: wrap;

  a {
    font-weight: 600;
    color: inherit;
  }
`;

const chipPlaceholderStyle: CSSProperties = {
  height: 28,
  width: 140,
  borderRadius: 14,
};

export const LaneInfo = ({
  board,
  idea,
  style,
}: {
  board?: IdeaPreviewProjectBoard;
  idea?: IdeaPreviewIdea;
  style?: CSSProperties;
}) => {
  if (idea?._id && board?.laneObject?.name) {
    if (board.laneObject?.laneCount === 0) return null;
    return (
      <StyledLaneInfo style={style}>
        <Icon icon={CompassIcon} />
        <span>
          Lane:{" "}
          <Popup
            disabled={!board.movedAt}
            trigger={
              <Link
                to={`/challenges/${idea?.challenge}/ideas?queryView=board&idea=${idea?._id}`}
              >
                {board.laneObject.name}
              </Link>
            }
          >{`${board.movedAt ? `Moved ${formatDistanceToNow(new Date(board.movedAt), { addSuffix: true })}` : ""} ${board.movedBy ? ` by ${board.movedBy?.profile?.fullName}` : ""}`}</Popup>
        </span>
      </StyledLaneInfo>
    );
  }
  return null;
};

type IdeaHeaderProps = {
  isEditing: boolean;
  updateIdea: (data: unknown, path: string[], save?: boolean) => void;
};

const IdeaHeader = ({ isEditing, updateIdea }: IdeaHeaderProps) => {
  const [idea, ideaQuery] = useIdea();
  const loading = ideaQuery.isLoading;
  const user = useCurrentUser();
  const { t } = useTranslation();
  const [labelChooserOpen, setLabelChooserOpen] = useState(false);

  const ideaId = idea?._id;
  const canManageChallenge = util.canManageChallenge(
    user!,
    idea?.ownerChallenge as IdeaPreviewOwnerChallenge | undefined,
  );
  const canEditIdeaLabels = util.hasPermission(
    user!,
    "challenge.editIdeaLabels",
    idea?.challenge,
  );

  const labelIdea = useCallback(
    (label: string, isApplied: boolean) => {
      if (!ideaId) return;
      let labels = idea?.labels || [];
      if (labels.includes(label)) {
        if (!isApplied) labels = labels.filter((l) => l !== label);
      } else if (isApplied) {
        labels = [...labels, label];
      }

      api.ideas.updateStatus(
        ideaId,
        { labels },
        () => updateIdea(labels, ["labels"], true),
        (err) => toast.error(err.message),
      );
    },
    [ideaId, idea?.labels, updateIdea],
  );

  if (isEditing) return null;

  if (loading || !idea) {
    return (
      <EntityHeaderContent style={{ zIndex: 200 }}>
        <TitleRow>
          <TitleBlock>
            <Placeholder>
              <PlaceholderLine style={{ height: 16, width: 220 }} />
            </Placeholder>
            <Placeholder>
              <PlaceholderLine style={{ height: 40, width: "60%" }} />
            </Placeholder>
          </TitleBlock>
        </TitleRow>
        <EntityMetaGrid>
          <EntityMetaBlock>
            <Placeholder>
              <PlaceholderLine style={{ height: 12, width: 70 }} />
            </Placeholder>
            <Placeholder style={{ gap: 8 }}>
              <PlaceholderLine style={chipPlaceholderStyle} />
              <PlaceholderLine style={chipPlaceholderStyle} />
            </Placeholder>
          </EntityMetaBlock>
          <EntityMetaSide>
            <EntityMetaBlock>
              <Placeholder>
                <PlaceholderLine style={{ height: 12, width: 50 }} />
              </Placeholder>
              <Placeholder style={{ gap: 6 }}>
                <PlaceholderLine
                  style={{ ...chipPlaceholderStyle, width: 80 }}
                />
                <PlaceholderLine
                  style={{ ...chipPlaceholderStyle, width: 80 }}
                />
              </Placeholder>
            </EntityMetaBlock>
          </EntityMetaSide>
        </EntityMetaGrid>
      </EntityHeaderContent>
    );
  }

  const board = idea?.projectManagement?.boards?.filter(
    (b) => b.forId === idea?.challenge,
  )[0] as IdeaPreviewProjectBoard | undefined;

  const hasCover =
    idea?.coverImageUrl &&
    !idea.coverImageUrl.includes("images/idea.png") &&
    !idea?.ownerChallenge?.preventIdeaCoverImages;

  const coverImage = hasCover
    ? util.ideaCoverImage(idea ?? undefined, "500x500")
    : undefined;

  return (
    <EntityHeaderContent style={{ zIndex: 200 }}>
      {canEditIdeaLabels ? (
        <LabelChooser
          isOpen={labelChooserOpen}
          onClose={() => setLabelChooserOpen(false)}
          onComplete={(em) => labelIdea(em, true)}
          existing={idea?.labels}
          onRemoveExisting={(em) => labelIdea(em, false)}
        />
      ) : null}

      <div>
        <TitleRow>
          <EntityCover
            image={coverImage}
            style={{ border: "none", boxShadow: "none", marginLeft: 0 }}
          />
          <TitleBlock>
            <ContextRow>
              {idea.ownerChallenge ? (
                <span>
                  {t("common:capitalise", { key: "generic.idea" })} for{" "}
                  <Link to={`/challenges/${idea.challenge}`}>
                    {idea.ownerChallenge.name}
                  </Link>
                </span>
              ) : (
                <span>{t("common:capitalise", { key: "generic.idea" })}</span>
              )}
              {canManageChallenge && board?.laneObject?.name ? (
                <>
                  <span style={{ color: "#c3c8d0" }}>/</span>
                  <LaneInfo board={board} idea={idea} />
                </>
              ) : null}
            </ContextRow>
            <H1>
              {idea.name ||
                `${t("common:capitalise", { key: "generic.ideas" })} ${idea.ownerChallenge?.name ? `for ${idea.ownerChallenge.name}` : ""}`}
            </H1>
          </TitleBlock>
        </TitleRow>

        <EntityMetaGrid>
          <EntityMetaBlock>
            <EntityMetaSectionLabel>
              {t("ideas.header.authors")}
            </EntityMetaSectionLabel>
            <EntityPeopleRow>
              {!idea.authors || idea.authors.length === 0 ? (
                <AnonymousChip />
              ) : null}
              {idea.lockedOwnerIdeaBusinessProfile ? (
                <IdeaBusinessProfileChip
                  ideaBusinessProfile={idea.lockedOwnerIdeaBusinessProfile}
                />
              ) : null}
              {(idea.authors || []).map((a) => (
                <UserChip key={a._id} style={{ margin: 0 }} user={a} />
              ))}
            </EntityPeopleRow>
          </EntityMetaBlock>

          <EntityMetaSide>
            {canEditIdeaLabels ? (
              <EntityMetaBlock>
                <EntityMetaSectionLabel>
                  {t("ideas.filters.labels")}
                </EntityMetaSectionLabel>
                <EntityMetaChips>
                  {idea?.labels?.map((label) => (
                    <LabelItem key={label} label={label} size="medium" />
                  ))}
                  <Button
                    size="small"
                    variant="outline"
                    onClick={() => setLabelChooserOpen(true)}
                  >
                    <Icon icon={BookmarkIcon} /> Edit labels
                  </Button>
                </EntityMetaChips>
              </EntityMetaBlock>
            ) : null}

            <EntityMetaBlock>
              <EntityMetaSectionLabel>
                {t("ideas.tags.title")}
              </EntityMetaSectionLabel>
              <EntityMetaChips>
                <IdeaTags idea={idea} updateIdea={updateIdea} noMargin />
              </EntityMetaChips>
            </EntityMetaBlock>
          </EntityMetaSide>
        </EntityMetaGrid>
      </div>
    </EntityHeaderContent>
  );
};

export default IdeaHeader;
